import {
  CopySimpleIcon,
  EyeIcon,
  EyeSlashIcon,
  GearSixIcon,
  MagnifyingGlassIcon,
  PauseIcon,
  PencilSimpleIcon,
  PlayCircleIcon,
  PlayIcon,
  PlusIcon,
  SparkleIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { endOfDay } from 'date-fns'
import { ChangeEvent, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useDeleteSurvey } from '@/api/communication/surveys/delete-survey'
import { getSurveyDetails } from '@/api/communication/surveys/get-survey-details'
import { useGetSurveyTemplatesStatus } from '@/api/communication/surveys/get-survey-templates-status'
import {
  SurveyHistoryItem,
  useGetSurveys,
  useInvalidateSurveys,
} from '@/api/communication/surveys/get-surveys'
import { useResumeSurvey } from '@/api/communication/surveys/resume-survey'
import { useStopSurvey } from '@/api/communication/surveys/stop-survey'
// TODO: refactor
import Dashboard from '@/components/Dashboard'
import { PageHeader } from '@/components/layout/page-header'
import { TableGroup } from '@/components/layout/table-group'
import QRCodeSurveysModal from '@/components/Surveys/QRCodeSurveysModal'
// TODO: refactor
import SurveyAnalytics from '@/components/Surveys/SurveyAnalytics'
// TODO: refactor
import SurveyFormModal from '@/components/Surveys/SurveyFormModal'
import SurveySettings from '@/components/Surveys/SurveySettingsModal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { StatusTabOption, StatusTabs } from '@/components/ui/status-tabs'
import { RowAction } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import {
  useCaptureEvent,
  useDesignVersionToggle,
  useFilters,
  useLocalStorage,
  useSearchParams,
} from '@/hooks'
import { getSurveyStatusColor, SurveyStatusEnum } from '@/models'
import { DateRange } from '@/models/helpers'
import { selectActiveBuilding, selectBuildingsForFilter } from '@/reducers/buildingSelectorReducer'
import { selectUserData, selectUserRole } from '@/reducers/userDataReducer'
import { getApiErrorMessage } from '@/utils/error'

import { DeleteSurveyConfirmationPopup } from './delete-survey-confirmation-popup'
import {
  getSurveysTableColumns,
  SURVEY_ANALYTICS_STORAGE_KEY,
  SURVEYS_ID,
  SurveyStatusFilter,
  SurveyStatusFilterEnum,
  surveysTableFiltersSchema,
  surveysTableInitialFilters,
} from './surveys.constants'
import * as S from './surveys.styled'
import { VideoTutorialModal } from './video-tutorial-modal'

export const SurveysPage = () => {
  const { t } = useTranslation(['communication'])
  const { navigate } = routerFactory.useRouter()
  const invalidateSurveys = useInvalidateSurveys()
  const { captureEvent, captureDebouncedEvent } = useCaptureEvent()

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'surveys',
    screen: 'Surveys',
    to: { path: '/dashboard/survey/legacy' },
    version: 'new',
  })

  const [isAnalyticsOpen, setIsAnalyticsOpen] = useLocalStorage(SURVEY_ANALYTICS_STORAGE_KEY, true)
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildings = useSelector(selectBuildingsForFilter)
  const userRole = useSelector(selectUserRole)
  const userData = useSelector(selectUserData)

  const buildingId = activeBuilding?.id
  const timezone = activeBuilding?.timezone ?? 'America/Chicago'

  const { data: templatesData } = useGetSurveyTemplatesStatus({ buildingId })

  const searchParams = useSearchParams({
    initialState: surveysTableInitialFilters,
    schema: surveysTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: surveysTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const filtersState = tableFilters.filters
  const queryParams = {
    buildingId: buildingId!,
    fromDate: filtersState.fromDate,
    limit: filtersState.limit,
    query: filtersState.search?.trim() || undefined,
    skip: filtersState.skip ?? 0,
    status:
      filtersState.status !== SurveyStatusFilterEnum.enum.All ? filtersState.status : undefined,
    toDate: filtersState.toDate,
  }

  const { data, isFetching } = useGetSurveys(queryParams)

  const statusTabOptions: StatusTabOption<SurveyStatusFilter>[] = useMemo(() => {
    return [
      {
        count:
          (data?.countScheduled ?? 0) +
          (data?.countOngoing ?? 0) +
          (data?.countCompleted ?? 0) +
          (data?.countOnHold ?? 0),
        label: t('communication:surveys.list.status.all'),
        value: SurveyStatusFilterEnum.enum.All,
      },
      {
        color: getSurveyStatusColor(SurveyStatusEnum.enum.Scheduled),
        count: data?.countScheduled ?? 0,
        label: t('communication:surveys.list.status.scheduled'),
        value: SurveyStatusFilterEnum.enum.Scheduled,
      },
      {
        color: getSurveyStatusColor(SurveyStatusEnum.enum.Completed),
        count: data?.countCompleted ?? 0,
        label: t('communication:surveys.list.status.completed'),
        value: SurveyStatusFilterEnum.enum.Completed,
      },
      {
        color: getSurveyStatusColor(SurveyStatusEnum.enum['On hold']),
        count: data?.countOnHold ?? 0,
        label: t('communication:surveys.list.status.onHold'),
        value: SurveyStatusFilterEnum.enum['On hold'],
      },
      {
        color: getSurveyStatusColor(SurveyStatusEnum.enum.Ongoing),
        count: data?.countOngoing ?? 0,
        label: t('communication:surveys.list.status.ongoing'),
        value: SurveyStatusFilterEnum.enum.Ongoing,
      },
    ]
  }, [data, t])

  const { mutateAsync: deleteSurvey } = useDeleteSurvey()
  const { mutateAsync: stopSurvey } = useStopSurvey()
  const { mutateAsync: resumeSurvey } = useResumeSurvey()

  const columns = useMemo(() => getSurveysTableColumns(t, timezone), [t, timezone])

  const fromDateFilter = tableFilters.getFilter('fromDate')
  const toDateFilter = tableFilters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]

  const handleSettingsClick = () => {
    popupFactory.open(SurveySettings, { buildingId })
  }

  const handleQrCodeSurveysClick = () => {
    popupFactory.open(QRCodeSurveysModal, {
      isExternal: activeBuilding?.qrCodeSurveysAvailable,
      userEmail: userData?.user?.email ?? '',
    })
  }

  const handleWatchTutorialClick = () => {
    popupFactory.open(VideoTutorialModal, {})
  }

  const handleSurveyFormModalClick = () => {
    popupFactory.open(SurveyFormModal, {
      buildings: buildings,
      currentSurvey: null,
      isDuplicateForm: false,
      onSuccess: invalidateSurveys,
      selectedBuilding: activeBuilding,
      userRole: userRole,
    })
  }

  const handleDateChange = (range: DateRange) => {
    const fromDate = range[0]?.getTime()
    const toDate = range[1] ? endOfDay(range[1]).getTime() : undefined
    tableFilters.setFilters({ fromDate, toDate })
    captureEvent('user_action', {
      fromDate,
      name: 'time_date_range_change',
      screen: SURVEYS_ID,
      toDate,
    })
  }

  const handleStatusChange = (value: SurveyStatusFilter) => {
    tableFilters.setFilter('status', value)
    captureEvent('user_action', {
      name: 'status_change',
      screen: SURVEYS_ID,
      value,
    })
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    tableFilters.setFilter('search', e.target.value)
    captureDebouncedEvent('user_action', {
      name: 'search_change',
      screen: SURVEYS_ID,
      value: e.target.value,
    })
  }

  const rowActions: RowAction<SurveyHistoryItem>[] = useMemo(
    () => [
      {
        icon: EyeIcon,
        label: t('communication:surveys.list.rowActions.details'),
        onClick: (survey) => {
          navigate({
            params: { id: survey.id },
            path: '/dashboard/survey/:id',
          })
        },
        primary: true,
      },
      {
        hidden: (survey) => survey.status === SurveyStatusEnum.enum.Completed,
        icon: PencilSimpleIcon,
        label: t('communication:surveys.list.rowActions.edit'),
        onClick: async (survey) => {
          try {
            // TODO: replace with useGetSurveyDetails query + loading state instead of direct fetch
            const surveyDetails = await getSurveyDetails({ id: survey.id })
            popupFactory.open(SurveyFormModal, {
              buildings: buildings,
              currentSurvey: surveyDetails,
              isDuplicateForm: false,
              onSuccess: invalidateSurveys,
              selectedBuilding: activeBuilding,
              userRole: userRole,
            })
          } catch (error) {
            toastFactory.error(getApiErrorMessage(error))
          }
        },
      },
      {
        icon: CopySimpleIcon,
        label: t('communication:surveys.list.rowActions.duplicate'),
        onClick: async (survey) => {
          try {
            // TODO: replace with useGetSurveyDetails query + loading state instead of direct fetch
            const surveyDetails = await getSurveyDetails({ id: survey.id })
            popupFactory.open(SurveyFormModal, {
              buildings: buildings,
              currentSurvey: surveyDetails,
              isDuplicateForm: true,
              onSuccess: invalidateSurveys,
              selectedBuilding: activeBuilding,
              userRole: userRole,
            })
          } catch (error) {
            toastFactory.error(getApiErrorMessage(error))
          }
        },
      },
      {
        hidden: (survey) =>
          survey.status === SurveyStatusEnum.enum.Completed ||
          survey.status === SurveyStatusEnum.enum['On hold'],
        icon: PauseIcon,
        label: t('communication:surveys.list.rowActions.stopSurvey'),
        onClick: async (survey) => {
          try {
            await stopSurvey({
              surveyId: survey.id,
              ...(!survey.isOneTime && {
                startTriggerDays: survey.startTriggerDays,
                startTriggerTime: survey.startTriggerTime,
                startTriggerType: survey.startTriggerType,
              }),
            })
            toastFactory.success(t('communication:surveys.list.stopSuccess', { name: survey.name }))
          } catch (error) {
            toastFactory.error(getApiErrorMessage(error))
          }
        },
      },
      {
        hidden: (survey) => survey.status !== SurveyStatusEnum.enum['On hold'],
        icon: PlayIcon,
        label: t('communication:surveys.list.rowActions.resumeSurvey'),
        onClick: async (survey) => {
          try {
            await resumeSurvey({
              surveyId: survey.id,
              ...(!survey.isOneTime && {
                startTriggerDays: survey.startTriggerDays,
                startTriggerTime: survey.startTriggerTime,
                startTriggerType: survey.startTriggerType,
              }),
            })
            toastFactory.success(
              t('communication:surveys.list.resumeSuccess', { name: survey.name }),
            )
          } catch (error) {
            toastFactory.error(getApiErrorMessage(error))
          }
        },
      },
      {
        icon: TrashIcon,
        label: t('communication:surveys.list.rowActions.delete'),
        onClick: (survey) => {
          popupFactory.open(DeleteSurveyConfirmationPopup, {
            onConfirm: async () => {
              try {
                await deleteSurvey({ surveyId: survey.id })
                toastFactory.success(
                  t('communication:surveys.list.deleteSuccess', { name: survey.name }),
                )
              } catch (error) {
                toastFactory.error(getApiErrorMessage(error))
              }
            },
            surveyName: survey.name,
          })
        },
        variant: 'danger',
      },
    ],
    [
      navigate,
      stopSurvey,
      resumeSurvey,
      deleteSurvey,
      buildings,
      activeBuilding,
      userRole,
      invalidateSurveys,
      t,
    ],
  )

  if (redirect) return redirect

  return (
    // TODO: Remove Dashboard.Container from here. It's added because surveys is outside of /building-dashboard routes. Padding and background color has to be provided to match rest of the pages.
    <Dashboard.Container className={`ctp-survey__wrapper`}>
      <PageHeader
        title={t('communication:surveys.list.title')}
        titleExtra={
          <>
            <Button size="small" use="upsell-secondary" onClick={handleWatchTutorialClick}>
              <Icon icon={PlayCircleIcon} />
              {t('communication:surveys.list.watchTutorial')}
            </Button>

            <Button
              size="small"
              use="secondary"
              onClick={() => setIsAnalyticsOpen(!isAnalyticsOpen)}
            >
              <Icon icon={isAnalyticsOpen ? EyeSlashIcon : EyeIcon} />
              {isAnalyticsOpen
                ? t('communication:surveys.list.hideAnalytics')
                : t('communication:surveys.list.showAnalytics')}
            </Button>
          </>
        }
        actions={
          <>
            <ToggleSwitch
              falsyLabel={t('communication:surveys.list.designToggle.old')}
              truthyLabel={t('communication:surveys.list.designToggle.new')}
              checked={true}
              onChange={handleUIToggle}
            />

            <Button size="small" use="ai" onClick={handleQrCodeSurveysClick}>
              <Icon icon={SparkleIcon} weight="fill" />
              {t('communication:surveys.list.qrCodeSurveys')}
              <Badge size="extra-small" color="purple">
                {t('communication:surveys.list.beta')}
              </Badge>
            </Button>

            {(templatesData?.length ?? 0) > 0 && (
              <Button size="small" use="secondary" onClick={handleSettingsClick}>
                <Icon icon={GearSixIcon} />
                {t('communication:surveys.list.settings')}
              </Button>
            )}
            <Button size="small" use="primary" onClick={handleSurveyFormModalClick}>
              {t('communication:surveys.list.createNew')}
              <Icon icon={PlusIcon} />
            </Button>
          </>
        }
      />

      {/* TODO: refactor SurveyAnalytics */}
      <S.AnalyticsCollapse in={isAnalyticsOpen}>
        <SurveyAnalytics buildingId={buildingId} isOpen={isAnalyticsOpen} />
      </S.AnalyticsCollapse>

      <TableGroup
        columns={columns}
        data={data?.surveys ?? []}
        getRowKey={(survey) => survey.id}
        id={SURVEYS_ID}
        loading={isFetching}
        navigation={{
          datePicker: (
            <DatePickerInput
              mode="range"
              placeholder={t('communication:surveys.list.dateRange.placeholder')}
              size="small"
              value={dateRange}
              onRangeChange={handleDateChange}
            />
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              name="search"
              placeholder={t('communication:surveys.list.search.placeholder')}
              size="small"
              value={tableFilters.getFilter('search')}
              onChange={handleSearchChange}
            />
          ),
          statusTabs: (
            <StatusTabs
              options={statusTabOptions}
              value={tableFilters.getFilter('status') ?? null}
              onChange={handleStatusChange}
            />
          ),
        }}
        pagination={{
          paginationFilters: tableFilters,
          total: data?.count ?? 0,
        }}
        rowActions={rowActions}
        tableFilters={tableFilters}
      />
    </Dashboard.Container>
  )
}
